import { clientConfigSchema, type ClientConfig } from '@/types';
import { PLATFORM_QUOTA_INITIAL_VALUES } from '@/services/platformSettingsService';

/**
 * FEAT-239 — single source of truth for the client-config editing surfaces.
 *
 * Every form-editable config field is declared ONCE here and consumed by:
 *   - ClientConfigForm (shared by Update + Create Client Config pages)
 *   - BulkUpdateClientConfig (field list derived from `bulk`/`allowUnset`)
 *   - ConfigHygienePanel (DEPRECATED_KEYS + unknown-key detection)
 *
 * Add new config fields HERE, not to the pages. A field left out of this
 * registry is invisible to the forms but untouched by saves (Update writes
 * a per-key diff, never the whole record).
 *
 * The form has five parts (CONFIG_SECTIONS):
 *   - Account: what every client must set (region, contract, apps).
 *   - Features: product features switched on or off. A feature's settings
 *     are its children (`childOf`) and show under it while it is on.
 *   - Chat: the chat model settings.
 *   - Advanced: infrastructure, quota sharing and rollback levers. Collapsed.
 *   - Arcanum internal: never for a customer. Collapsed, and shown only for an
 *     Internal contract or when one of its switches is already on.
 */

export type SectionId = 'account' | 'features' | 'chat' | 'advanced' | 'internal';

export interface ConfigGroup {
  id: string;
  title: string;
  description: string;
}

export interface ConfigSection {
  id: SectionId;
  title: string;
  description?: string;
  /** Starts closed on Create, and on Update unless a field in it differs from its default. */
  collapsed?: boolean;
  /** Sub-groups, in order. A top-level field names its group with `group`. */
  groups?: ConfigGroup[];
}

export const CONFIG_SECTIONS: ConfigSection[] = [
  { id: 'account', title: 'Account', description: 'Region, contract and apps. Every client needs these.' },
  {
    id: 'features',
    title: 'Features',
    description: 'Switch a feature on or off. Its settings open under it.',
    groups: [
      { id: 'workspace', title: 'Workspace', description: 'What people get in Numa every day.' },
      { id: 'integrations', title: 'Integrations', description: 'How Numa connects to other systems.' },
      { id: 'modules', title: 'Modules', description: 'Add-on modules.' },
      { id: 'files', title: 'Files', description: 'Sharing with people outside the company.' },
      {
        id: 'publicPages',
        title: 'Public pages',
        description:
          "Arcanum's limits on public links: their forms and their AI. A company admin sets lower limits in Settings > Public AI and asks Arcanum for more. Empty: the default.",
      },
      { id: 'security', title: 'Security and network', description: 'Sign-in and network controls.' },
    ],
  },
  { id: 'chat', title: 'Chat', description: 'The models people chat with.' },
  {
    id: 'advanced',
    title: 'Advanced',
    description: 'Change these only with a developer.',
    collapsed: true,
    groups: [
      {
        id: 'infrastructure',
        title: 'Infrastructure',
        description: 'Where and how the stack runs.',
      },
      {
        id: 'quota',
        title: 'Bedrock quota sharing',
        description: 'Accounts that share their Bedrock quotas with each other.',
      },
      {
        id: 'rollback',
        title: 'Emergency rollback',
        description: 'Levers that move a model back to its old route. Leave these alone unless a developer asks.',
      },
      {
        id: 'roles',
        title: 'Features by role',
        description: 'What standard users and admins get. Leave these as they are to use the recommended sets.',
      },
    ],
  },
  {
    id: 'internal',
    title: 'Arcanum internal',
    description: "For Arcanum's own stacks. Never switch these on for a customer.",
    collapsed: true,
  },
];

/** Sub-headings inside a feature's settings, for features with many (Automations). */
export const CHILD_GROUPS: Record<string, ConfigGroup> = {
  schedules: {
    id: 'schedules',
    title: 'Schedules',
    description:
      'Automations that run on a timetable. A schedule counts against the limits by how often it is set to run.',
  },
  triggers: {
    id: 'triggers',
    title: 'Event triggers',
    description:
      'Automations that an outside event starts, such as a Slack message or an email. Each run counts against the limits.',
  },
  concurrency: {
    id: 'concurrency',
    title: 'Concurrency limits',
    description: 'How many automations can be switched on at once, schedules and event triggers together.',
  },
};

/** Form-level value for a single field. tri-state selects use '' | 'true' | 'false'. */
export type FormFieldValue = string | boolean;
export type FormValues = Record<string, FormFieldValue>;

export type ConfigFieldType = 'switch' | 'text' | 'number' | 'select' | 'tri-state';

export interface FieldDef {
  key: string;
  label: string;
  section: SectionId;
  /** Top-level fields only: the sub-group of the section the field shows in. */
  group?: string;
  type: ConfigFieldType;
  /** UI display default — what the field shows when the key is absent from the record. */
  default: FormFieldValue;
  /**
   * Create-page initial value when it should differ from the schema default
   * (e.g. flags ~95% of the fleet turns on). Because it differs from
   * `default`, Create writes the key EXPLICITLY — deploy-time schema defaults
   * are unchanged, so existing records missing the key are unaffected.
   */
  createDefault?: FormFieldValue;
  /** The default in words for the label line ("default Sydney"), when the option's own label is too long. */
  defaultLabel?: string;
  help?: string;
  options?: { label: string; value: string }[];
  placeholder?: string;
  /** Live "Leave empty to inherit (Platform Settings → N)" placeholder for quota fields. */
  inheritKey?: keyof typeof PLATFORM_QUOTA_INITIAL_VALUES;
  inheritSuffix?: string;
  /** Hide the field unless the predicate over current form values passes. */
  visibleWhen?: (values: FormValues) => boolean;
  /** Grey the control out (still visible, not editable) while the predicate over current form values passes. */
  disabledWhen?: (values: FormValues) => boolean;
  /** Info note rendered under the field while `disabledWhen` holds, explaining why it is disabled. */
  disabledNote?: string;
  /**
   * `switch` fields only: when a sibling change makes `disabledWhen` hold, force
   * the value OFF instead of leaving it stuck visually "On" while greyed. Use for
   * toggles that are meaningless once disabled. Mirrors infra's derived flag,
   * which already gates the runtime value on the same condition.
   */
  disabledForcesOff?: boolean;
  /**
   * Update page only: the control is read-only, with this note under it. For a
   * value that is fixed when the client is created.
   */
  lockedOnUpdate?: string;
  /**
   * `select` fields whose option list depends on other form values (e.g. the
   * default-model dropdown narrows with Bedrock Models Only). When present it
   * overrides the static `options`. The form also keeps the field's value valid:
   * if a sibling change drops the current choice from the list, it snaps to the
   * first enabled option.
   */
  dynamicOptions?: (values: FormValues) => { label: string; value: string; disabled?: boolean }[];
  /**
   * Custom "effective current value" for diffing — what the deployment uses
   * today when the key is absent. Use for fields whose absent-value is DERIVED
   * from sibling flags, so a pristine record shows no spurious pending change.
   * Must agree with `hydrate` for the same record.
   */
  effectiveCurrent?: (config: Partial<ClientConfig> | undefined) => unknown;
  /**
   * Key of the parent field this one belongs to. Children render inside an
   * indented sub-panel directly under their parent, at any depth. Combine with
   * `visibleWhen` to gate visibility. A child keeps its parent's `section`.
   */
  childOf?: string;
  /** Children only: the sub-heading (CHILD_GROUPS) the child shows under. */
  childGroup?: keyof typeof CHILD_GROUPS;
  /** Spans both columns of the form's grid (a long control, or a warning that needs the room). */
  wide?: boolean;
  /** The form draws this field with its own control, not the generic one (contractType). */
  bespoke?: boolean;
  /** Custom hydration from a config record (e.g. legacy-key fallbacks). */
  hydrate?: (config: Partial<ClientConfig> | undefined) => FormFieldValue;
  /** Update mode: always write the key when missing from the record, even at default. */
  alwaysWrite?: boolean;
  /** Include in the Bulk Update tool. Defaults to true. */
  bulk?: boolean;
  /** Validation for text/number fields, run on submit against the raw string. */
  validate?: (raw: string) => string | null;
  /** Warning Alert rendered under the field whenever it holds a non-default value. */
  warnWhenSet?: string;
  /**
   * Create page only: every new client must hold this key. The form does not
   * save while it is empty, and the value is always written, even when it
   * equals the deploy-time default (so a record that lacks the key is an
   * existing client nobody has set yet, never a new one).
   */
  requiredOnCreate?: boolean;
}

// ---------------------------------------------------------------------------
// Workspace chat model tiers — default-model dropdown + picker availability.
//
// Bedrock Models Only constrains the AVAILABLE set (drops Lite while it is on
// the relay); Default Model picks the platform default from that set, or
// leaves it to the platform; the end-user picker ("Workspace Chat Model
// Selection") lets people switch between the available tiers.
//
// Keep the tiers / derivation / availability IN SYNC with:
//   - infra/stacks/numa-client-stack.ts (emits DEFAULT_CHAT_MODEL + the derived
//     WORKSPACE_CHAT_MODEL_SELECTION picker flag)
//   - services/numa-workspace-agent/.../sdk_config.py (platform_default_model)
//   - numa-frontend workspaceChatTypes + ChatInput (curated selector + filter)
// ---------------------------------------------------------------------------

export type ChatModelTier = 'light' | 'standard' | 'premium' | 'expert';

/** Dropdown labels for each tier. Lite = Numa Lite Model (GPT-6 Luna on Bedrock
 *  since 2026-09-22); Standard = Claude Haiku 5.5 at medium effort; Premium =
 *  Sonnet 5.5 at medium effort and Expert = Opus 5.5 at high effort, all on AWS
 *  Bedrock. */
export const CHAT_MODEL_TIER_LABELS: Record<ChatModelTier, string> = {
  light: 'Lite — GPT-6 Luna (cheapest, AWS Bedrock)',
  standard: 'Standard — Haiku 5.5 (everyday, AWS Bedrock)',
  premium: 'Premium — Sonnet 5.5 (AWS Bedrock)',
  expert: 'Expert — Opus 5.5 high effort (AWS Bedrock)',
};

const CHAT_MODEL_TIER_NAMES: Record<ChatModelTier, string> = {
  light: 'Lite',
  standard: 'Standard',
  premium: 'Premium',
  expert: 'Expert',
};

/** The Premium label on a record with global inference profiles off. Sonnet 5.5
 *  has only a `global.` profile, so the workspace agent runs Premium there on
 *  Opus 5.5 at low effort, on the regional `us.` / `au.` profile. */
export const PREMIUM_REGIONAL_TIER_LABEL = 'Premium — Opus 5.5 low effort (AWS Bedrock, regional)';

/** The Standard label on a record with global inference profiles off. Standard
 *  is Claude Haiku 5.5 at medium effort on every record (since 2026-10-19); here
 *  it runs on the regional `us.` / `au.` profile instead of `global.`. */
export const STANDARD_REGIONAL_TIER_LABEL = 'Standard — Haiku 5.5 (AWS Bedrock, regional)';

/** The Premium label for this record: the regional one when global inference
 *  profiles are off. */
function premiumTierLabel(values: FormValues): string {
  return values.useGlobalInferenceProfile === false ? PREMIUM_REGIONAL_TIER_LABEL : CHAT_MODEL_TIER_LABELS.premium;
}

/** The Standard label for this record: the regional one when global inference
 *  profiles are off. */
function standardTierLabel(values: FormValues): string {
  return values.useGlobalInferenceProfile === false ? STANDARD_REGIONAL_TIER_LABEL : CHAT_MODEL_TIER_LABELS.standard;
}

/** Whether a tier is offered given the Bedrock Models Only, global-profile and
 *  transport flags. Standard is Claude Haiku 5.5 on Bedrock, on the `global.`
 *  profile or the regional `us.` / `au.` one, so every record offers it, and no
 *  transport switch moves it. Lite runs GPT-6 Luna on a `global.` Bedrock
 *  profile — the only OpenAI profile outside the US regions — so it needs global
 *  profiles, and Bedrock Models Only drops it while its transport is the relay.
 *  Mirrors infra's resolveChatModelConfig. */
function tierAllowed(values: FormValues, tier: ChatModelTier): boolean {
  if (tier !== 'light') return true;
  if (values.lightModelTransport === 'relay') return values.bedrockModelsOnly !== true;
  return values.useGlobalInferenceProfile !== false;
}

/** Whether the client surfaces the Expert (Opus) tier. Mirrors the chat gating
 *  (Expert is developer-mode only). The portal form has `devInstance` but not
 *  `publicDemo`, so this is an admin-side approximation of DEVELOPER_MODE
 *  (= devInstance && !publicDemo); the runtime authority stays the frontend. */
function expertTierAvailable(values: FormValues): boolean {
  return values.devInstance === true;
}

/** The model tiers available to a client given its flags, cheapest-first.
 *  Bedrock Models Only drops Lite while Lite is on the relay. */
export function availableChatModelTiers(values: FormValues): ChatModelTier[] {
  const tiers: ChatModelTier[] = [];
  if (tierAllowed(values, 'light')) tiers.push('light');
  if (tierAllowed(values, 'standard')) tiers.push('standard');
  tiers.push('premium');
  if (expertTierAvailable(values)) tiers.push('expert');
  return tiers;
}

/** Options for the Default Model dropdown. The first is the platform default:
 *  it saves nothing, so the client follows whatever the release decides, and it
 *  names the tier that is today. Standard and Premium are always offered; Lite
 *  is dropped where it is not allowed; Expert (Opus) is always shown but GREYED
 *  OUT unless this is a Development instance, so admins can see the tier exists
 *  and why it is gated. The greyed (disabled) tiers are never selectable and the
 *  form snaps off them. */
export function chatModelDropdownOptions(values: FormValues): { label: string; value: string; disabled?: boolean }[] {
  const dev = expertTierAvailable(values);
  const platform = resolveDefaultChatModel({ ...values, defaultChatModel: '' });
  const options: { label: string; value: string; disabled?: boolean }[] = [
    { label: `Platform default (now ${CHAT_MODEL_TIER_NAMES[platform]})`, value: '' },
  ];
  if (tierAllowed(values, 'light')) options.push({ label: CHAT_MODEL_TIER_LABELS.light, value: 'light' });
  if (tierAllowed(values, 'standard')) options.push({ label: standardTierLabel(values), value: 'standard' });
  options.push({ label: premiumTierLabel(values), value: 'premium' });
  options.push({
    label: dev ? CHAT_MODEL_TIER_LABELS.expert : `${CHAT_MODEL_TIER_LABELS.expert} — Development instances only`,
    value: 'expert',
    disabled: !dev,
  });
  return options;
}

/** The tier a record resolves to when `defaultChatModel` is absent: **Standard**
 *  (the everyday tier) unless Bedrock Models Only is on or global inference
 *  profiles are off — then Premium. That exception is kept deliberately: a
 *  governance tenant's default model must never swap silently — set
 *  `defaultChatModel: standard` to opt in. Independent of the picker toggle. Lite
 *  is opt-in only (never the derived default). Accepts form values or a raw record.
 *  Mirrors infra's resolveChatModelConfig. */
export function resolveDefaultChatModel(source: FormValues | Partial<ClientConfig> | undefined): ChatModelTier {
  const v = (source ?? {}) as Record<string, unknown>;
  const flags = {
    bedrockModelsOnly: v.bedrockModelsOnly === true,
    useGlobalInferenceProfile: v.useGlobalInferenceProfile !== false,
  };
  const explicit = v.defaultChatModel;
  const wanted: ChatModelTier =
    explicit === 'light' || explicit === 'standard' || explicit === 'premium' || explicit === 'expert'
      ? explicit
      : flags.bedrockModelsOnly || !flags.useGlobalInferenceProfile
        ? 'premium'
        : 'standard';
  // Clamp into the offered set exactly as infra does (cheapest offered tier),
  // so the portal never shows a default the stack will not honour — e.g. an
  // explicit 'light' on a Bedrock-only record resolves to Standard, and an
  // explicit 'light' on a record without global profiles resolves to Standard.
  if (tierAllowed(flags, wanted)) return wanted;
  const ladder: ChatModelTier[] = ['light', 'standard', 'premium'];
  return ladder.find((t) => tierAllowed(flags, t)) ?? 'premium';
}

/** The Default Chat Model a record SAVED, clamped as infra clamps it, or '' when
 *  it saved none and follows the platform default. */
function savedDefaultChatModel(config: Partial<ClientConfig> | undefined): ChatModelTier | '' {
  const explicit = (config as Record<string, unknown> | undefined)?.defaultChatModel;
  if (explicit !== 'light' && explicit !== 'standard' && explicit !== 'premium' && explicit !== 'expert') return '';
  return resolveDefaultChatModel(config);
}

// ---------------------------------------------------------------------------
// Shared option constants (previously duplicated in Update/Create/Bulk pages)
// ---------------------------------------------------------------------------

export const REGION_OPTIONS = [
  { label: 'Asia Pacific (Sydney) ap-southeast-2', value: 'ap-southeast-2' },
  { label: 'US East (N. Virginia) us-east-1', value: 'us-east-1' },
  { label: 'Asia Pacific (Jakarta) ap-southeast-3', value: 'ap-southeast-3' },
];

/** The region a new client starts in. */
export const DEFAULT_REGION = 'ap-southeast-2';

// Regions where Bedrock AgentCore is available (for cross-region AgentCore deployments)
export const AGENTCORE_REGION_OPTIONS = [
  { label: 'US East (N. Virginia) us-east-1', value: 'us-east-1' },
  { label: 'Asia Pacific (Sydney) ap-southeast-2', value: 'ap-southeast-2' },
  { label: 'Asia Pacific (Singapore) ap-southeast-1', value: 'ap-southeast-1' },
  { label: 'Asia Pacific (Tokyo) ap-northeast-1', value: 'ap-northeast-1' },
  { label: 'Asia Pacific (Seoul) ap-northeast-2', value: 'ap-northeast-2' },
  { label: 'Asia Pacific (Mumbai) ap-south-1', value: 'ap-south-1' },
];

// All apps from infrastructure appLibrary + V2_MIGRATED_APPS + devAppLibrary (sorted alphabetically)
// Matches infra/stacks/numa-client-stack.ts appLibrary, V2_MIGRATED_APPS and devAppLibrary
export const ALL_APPS = [
  'beyond-expectations',
  'candidate-screening',
  'company-profile',
  'contract-analysis',
  'council-recourse-consents',
  'costing-calculator',
  'data-analysis',
  'document-summariser',
  'e2e-test',
  'financial-analysis',
  'gdsr-assessment',
  'infringement-review',
  'meeting-analyser',
  'nolia',
  'policy-designer',
  'policy-drafter',
  'policy-reviewer',
  'procurement-rfp-assessment',
  'quoting',
  'rfp-response-comparison',
  'structured-data-query',
  'synergy-explorer',
  'tor-assessment',
];

// Synergy Explorer is not a production app — it only ever deploys from an
// explicit `apps` map entry, and infra drops it unless synergy is enabled
// (synergyEnabled = synergy && dataConnectorsEnabled). The form keeps this app
// tied to the `synergy` toggle so the two can never disagree.
export const SYNERGY_APP_ID = 'synergy-explorer';

// Production apps only (isProdApp: true in infra/stacks/numa-client-stack.ts —
// both `appLibrary` and `V2_MIGRATED_APPS`, which carries its own isProdApp flag).
export const PROD_APPS = [
  'candidate-screening',
  'contract-analysis',
  'data-analysis',
  'document-summariser',
  'financial-analysis',
  'meeting-analyser',
  'policy-drafter',
  'policy-reviewer',
];

// ---------------------------------------------------------------------------
// Field validation helpers
// ---------------------------------------------------------------------------

const validateMinInterval = (raw: string): string | null => {
  if (!raw) return null;
  const val = parseInt(raw, 10);
  if (isNaN(val) || val < 5 || val > 1440) {
    return 'Minimum Automation Interval must be a whole number between 5 and 1440 minutes';
  }
  return null;
};

const validatePositiveInt =
  (label: string) =>
  (raw: string): string | null => {
    if (!raw) return null;
    const n = parseInt(raw, 10);
    if (isNaN(n) || !Number.isInteger(n) || n <= 0) return `${label} must be a positive integer`;
    return null;
  };

/** A whole number from `min` to `max`, or empty (the default). */
const validateIntRange =
  (label: string, min: number, max: number) =>
  (raw: string): string | null => {
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isInteger(n) || n < min || n > max) {
      return `${label} must be a whole number from ${min.toLocaleString()} to ${max.toLocaleString()}`;
    }
    return null;
  };

const validateVoiceRecordingMinutes = (raw: string): string | null => {
  if (!raw) return null;
  const val = Number(raw);
  if (!Number.isInteger(val) || val < 1 || val > 240) {
    return 'Voice Recording Default Limit must be a whole number between 1 and 240 minutes';
  }
  return null;
};

const validateAccountId = (raw: string): string | null =>
  raw === '' || /^\d{12}$/.test(raw) ? null : 'The quota account must be a 12-digit AWS account ID';

const schedulingOn = (v: FormValues) => v.scheduling === true;
const triggersOn = (v: FormValues) => v.scheduling === true && v.eventTriggers === true;
const mobilePushOn = (v: FormValues) => v.mobilePushEnabled === true;
// Amazon Nova 2 Sonic, which voice mode talks through, runs only here and has
// no cross-region profile. Anywhere else the switch would do nothing, so it is
// not offered.
const VOICE_MODE_REGIONS = ['us-east-1', 'us-west-2', 'eu-north-1', 'ap-northeast-1'];
const voiceModeRegion = (v: FormValues) => VOICE_MODE_REGIONS.includes(String(v.region ?? ''));
const contactsOn = (v: FormValues) => v.contacts === true;
// Two levels, not one: everything below telephony needs an Amazon Connect
// instance to mean anything, so it stays hidden until telephony itself is on.
const contactsTelephonyOn = (v: FormValues) => v.contacts === true && v.contactsTelephony === true;

/**
 * The Files search backend a record runs on. Infra derives it from Provision Q
 * Resources when the key is absent (q when on, bedrock when off), so the form
 * reads it the same way. Reading an absent key as bedrock moved a Q client to
 * Bedrock on its next unrelated save.
 */
const effectiveKnowledgeBase = (config: Partial<ClientConfig> | undefined): string => {
  const stored = (config as Record<string, unknown> | undefined)?.preferredKnowledgeBase;
  if (typeof stored === 'string') return stored;
  return config?.provisionQResources === true ? 'q' : 'bedrock';
};

// ---------------------------------------------------------------------------
// The registry. On-screen order within a section (and a group) = array order.
// The contract control (for `contractType`), the apps deployment control and
// the role checklists are bespoke (see ClientConfigForm). Everything else
// renders from here.
// ---------------------------------------------------------------------------

export const FIELDS: FieldDef[] = [
  // ─── Account ───────────────────────────────────────────────────────────────
  {
    key: 'region',
    label: 'Region',
    section: 'account',
    type: 'select',
    default: 'us-east-1',
    createDefault: DEFAULT_REGION,
    defaultLabel: 'Sydney',
    options: REGION_OPTIONS,
    lockedOnUpdate:
      'The region is set when the client is created. Moving a client to another region builds a new stack: ask a developer.',
    bulk: false,
    help: 'The AWS region the stack runs in.',
  },
  {
    // Drawn by the contract control in the Account section: the person picks a
    // contract and the form saves the billing it means. On a Partner contract
    // this value is the "Billed as" choice.
    key: 'contractType',
    label: 'Billed as',
    section: 'account',
    type: 'select',
    default: 'legacy',
    createDefault: 'credits',
    options: [
      { label: 'Credits', value: 'credits' },
      { label: 'Legacy', value: 'legacy' },
      { label: 'Own account', value: 'own-account' },
    ],
    bespoke: true,
    alwaysWrite: true,
    // A contract is set with its record in the portal's table, which the bulk
    // tool does not write. The Credits page sets it for many clients.
    bulk: false,
    help: "How the client is billed. The portal saves it from the contract: Trial and Internal bill as Credits, and a Partner client is billed as chosen. Credits and Own account let the client's admins choose the model new chats and automations start on. Own account: Numa runs in the client's own AWS account and the client pays AWS directly, so its people see real AWS dollars, no credit limit pauses anything and the credit overage alerts leave it out. Legacy: the default-model settings are hidden and the Default Chat Model applies.",
  },
  {
    key: 'showCredits',
    label: 'Show credits in the app',
    section: 'account',
    type: 'switch',
    default: true,
    help: "The client's billing admins see the Credits page and the credit count in chat. Usage is metered either way. Pricing is set on the Credits page.",
  },
  {
    key: 'appBuilder',
    label: 'App Builder',
    section: 'account',
    type: 'switch',
    default: false,
    help: 'People build interactive apps by talking to the App Builder. Without it they can publish dashboards only.',
  },

  // ─── Features › Workspace ──────────────────────────────────────────────────
  {
    key: 'agents',
    label: 'Agents',
    section: 'features',
    group: 'workspace',
    type: 'switch',
    default: false,
    createDefault: true, // 93% of production clients have it on
    help: 'People build their own agents and share them.',
  },
  {
    key: 'scheduling',
    label: 'Automations',
    section: 'features',
    group: 'workspace',
    type: 'switch',
    default: false,
    createDefault: true, // TASK-524: on for every new account; existing records keep the deploy-time default
    help: 'Agents run on a schedule or when something happens, and send notifications. The limits under it apply while it is on.',
  },
  {
    key: 'schedulingMinIntervalMinutes',
    label: 'Minimum interval (minutes)',
    section: 'features',
    childOf: 'scheduling',
    childGroup: 'schedules',
    type: 'number',
    default: '',
    visibleWhen: schedulingOn,
    inheritKey: 'schedulingMinIntervalMinutes',
    inheritSuffix: ' min',
    validate: validateMinInterval,
    help: 'The shortest time allowed between two runs of a schedule.',
  },
  {
    key: 'maxRunsPerCompanyPerMonth',
    label: 'Schedule runs a month, company',
    section: 'features',
    childOf: 'scheduling',
    childGroup: 'schedules',
    type: 'number',
    default: '',
    visibleWhen: schedulingOn,
    inheritKey: 'maxRunsPerCompanyPerMonth',
    validate: validatePositiveInt('Schedule runs a month, company'),
    help: 'A hard cap for the whole company. A schedule above it is refused, whatever an admin approves.',
  },
  {
    key: 'maxRunsPerUserPerMonth',
    label: 'Schedule runs a month, person',
    section: 'features',
    childOf: 'scheduling',
    childGroup: 'schedules',
    type: 'number',
    default: '',
    visibleWhen: schedulingOn,
    inheritKey: 'maxRunsPerUserPerMonth',
    validate: validatePositiveInt('Schedule runs a month, person'),
    help: 'Above this, a schedule needs admin approval (when approval is on) or is refused.',
  },
  {
    key: 'requireApprovalAboveUserCap',
    label: 'Admin approval above the person limit',
    section: 'features',
    childOf: 'scheduling',
    childGroup: 'schedules',
    type: 'tri-state',
    default: '',
    defaultLabel: 'platform setting',
    visibleWhen: schedulingOn,
    options: [
      { label: 'Platform setting (on)', value: '' },
      { label: 'On: requests over the limit go to an admin', value: 'true' },
      { label: 'Off: requests over the limit are refused', value: 'false' },
    ],
    help: 'An admin can approve up to the company limit, never above it.',
  },
  {
    key: 'eventTriggers',
    label: 'Event triggers',
    section: 'features',
    childOf: 'scheduling',
    childGroup: 'triggers',
    type: 'switch',
    default: false,
    // Ian and Asa, 2026-10-22: new clients get triggers, as they get schedules.
    createDefault: true,
    visibleWhen: schedulingOn,
    // Read from eventTriggers first (authoritative post-rebase), fall back to
    // the legacy `triggers` key for unmigrated records, then default false.
    hydrate: (cfg) => {
      const c = cfg as { eventTriggers?: boolean; triggers?: boolean } | undefined;
      return c?.eventTriggers ?? c?.triggers ?? false;
    },
    help: 'Automations that start when something happens. Off hides the trigger builder and its admin tab. Schedules keep working.',
  },
  {
    key: 'maxTriggerRunsPerCompanyPerMonth',
    label: 'Trigger runs a month, company',
    section: 'features',
    childOf: 'eventTriggers',
    type: 'number',
    default: '',
    visibleWhen: triggersOn,
    inheritKey: 'maxTriggerRunsPerCompanyPerMonth',
    validate: validatePositiveInt('Trigger runs a month, company'),
    help: 'A hard cap for the whole company, counted when a trigger fires.',
  },
  {
    key: 'maxTriggerRunsPerUserPerMonth',
    label: 'Trigger runs a month, person',
    section: 'features',
    childOf: 'eventTriggers',
    type: 'number',
    default: '',
    visibleWhen: triggersOn,
    inheritKey: 'maxTriggerRunsPerUserPerMonth',
    validate: validatePositiveInt('Trigger runs a month, person'),
    help: 'A fire over the limit is dropped, and the owner gets one notice that month.',
  },
  {
    key: 'maxConcurrentActiveSchedulesPerCompany',
    label: 'Automations on at once, company',
    section: 'features',
    childOf: 'scheduling',
    childGroup: 'concurrency',
    type: 'number',
    default: '',
    visibleWhen: schedulingOn,
    inheritKey: 'maxConcurrentActiveSchedulesPerCompany',
    validate: validatePositiveInt('Automations on at once, company'),
    help: 'A hard cap for the whole company: schedules and event triggers together.',
  },
  {
    key: 'maxConcurrentActiveSchedulesPerUser',
    label: 'Automations on at once, person',
    section: 'features',
    childOf: 'scheduling',
    childGroup: 'concurrency',
    type: 'number',
    default: '',
    visibleWhen: schedulingOn,
    inheritKey: 'maxConcurrentActiveSchedulesPerUser',
    validate: validatePositiveInt('Automations on at once, person'),
    help: 'The cap for one person.',
  },
  {
    key: 'homeDashboard',
    label: 'Home page',
    section: 'features',
    group: 'workspace',
    type: 'switch',
    // Off everywhere until someone chooses it. No createDefault override: this
    // moves where a customer's people land after signing in, which is a
    // decision for whoever knows the customer, not a default they inherit.
    default: false,
    help: 'People land on Home after they sign in: what needs them, how their automations run, recent activity, and what they or an admin pinned.',
  },
  {
    key: 'homeChatSignals',
    label: 'Chats waiting on the person',
    section: 'features',
    childOf: 'homeDashboard',
    type: 'switch',
    default: false,
    visibleWhen: (v) => v.homeDashboard === true,
    help: 'Home lists chats where Numa waits on a reply. An hourly pass judges each chat active in the last week.',
  },
  {
    key: 'voiceMode',
    label: 'Voice mode',
    section: 'features',
    group: 'workspace',
    type: 'switch',
    default: false,
    visibleWhen: voiceModeRegion,
    help: 'People talk to Numa out loud and hear it answer. Offered only in us-east-1, where Amazon Nova 2 Sonic runs. 0.1 credits for each minute anyone talks.',
  },
  {
    key: 'brandingProviderEnabled',
    label: 'Branding',
    section: 'features',
    group: 'workspace',
    type: 'switch',
    default: false,
    createDefault: true, // 98% of production clients have it on; none ever off
    help: "Admins set the company's logo and colours.",
  },
  {
    key: 'mobilePushEnabled',
    label: 'Mobile push notifications',
    section: 'features',
    group: 'workspace',
    type: 'switch',
    default: false,
    help: "The Numa phone app sends push notifications. Off hides the phone app's push control.",
  },
  {
    key: 'expoAccessToken',
    label: 'Expo access token',
    section: 'features',
    childOf: 'mobilePushEnabled',
    type: 'text',
    default: '',
    visibleWhen: mobilePushOn,
    help: 'Only when the Expo project has enhanced push security on. Leave it empty otherwise.',
  },

  // ─── Features › Integrations ───────────────────────────────────────────────
  {
    key: 'pipedreamIntegrations',
    label: 'Pipedream integrations',
    section: 'features',
    group: 'integrations',
    type: 'switch',
    default: false,
    createDefault: true, // 92% of production clients have it on
    help: 'People connect SaaS apps such as Gmail, Slack and HubSpot.',
  },
  {
    key: 'productDemoAgent',
    label: 'Product Demo',
    section: 'features',
    childOf: 'pipedreamIntegrations',
    type: 'switch',
    default: false,
    visibleWhen: (v) => v.pipedreamIntegrations === true,
    disabledWhen: (values) => values.pipedreamIntegrations !== true,
    disabledForcesOff: true,
    help: "A person uploads a screen recording and Numa makes a finished demo video with a new voiceover, plus a PDF guide. Each person connects their own HeyGen account for the voice, and HeyGen bills that account. The client's admin can turn it off in Settings > Capabilities.",
  },
  {
    key: 'dataConnectorsEnabled',
    label: 'Native integrations',
    section: 'features',
    group: 'integrations',
    type: 'switch',
    default: false,
    // Ian and Asa, 2026-10-22: on for new clients, as Pipedream is.
    createDefault: true,
    help: "Arcanum's own connectors (SharePoint, Google Drive, Xero and more) and the Secrets Vault.",
  },
  {
    key: 'synergy',
    label: 'Synergy 12d',
    section: 'features',
    childOf: 'dataConnectorsEnabled',
    type: 'switch',
    default: false,
    visibleWhen: (v) => v.dataConnectorsEnabled === true,
    help: 'The Synergy connector: the Files-page browser and the Synergy Explorer app.',
  },
  {
    key: 'cmsIsscConnector',
    label: 'CMS (ACC sensitive claims)',
    section: 'features',
    childOf: 'dataConnectorsEnabled',
    type: 'switch',
    default: false,
    visibleWhen: (v) => v.dataConnectorsEnabled === true,
    help: 'Clinicians connect their CMS calendar and ask about their own appointments. CMS holds ACC sensitive-claims records, so switch it on only after the practice agrees who may query it and what may leave Numa.',
  },

  // ─── Features › Modules ────────────────────────────────────────────────────
  {
    key: 'numaOps',
    label: 'Numa Ops',
    section: 'features',
    group: 'modules',
    type: 'switch',
    default: false,
    help: 'Boards, tickets, projects and the CRM.',
  },
  {
    key: 'partners',
    label: 'Partners in the Ops CRM',
    section: 'features',
    childOf: 'numaOps',
    type: 'switch',
    default: false,
    visibleWhen: (v) => v.numaOps === true,
    help: "Adds Partners to the Ops CRM, beside customers and suppliers. The client's own partners: nothing to do with the client's contract.",
  },
  {
    key: 'awsPartnerCentral',
    label: 'AWS Partner Central',
    section: 'features',
    childOf: 'numaOps',
    type: 'switch',
    default: false,
    visibleWhen: (v) => v.numaOps === true,
    help: "People send an Ops CRM customer to the client's AWS Partner Central as an opportunity, and see AWS's review and referrals on the customer. Needs the AWS Partner Central connection, which is set in the bulk tool.",
  },
  {
    key: 'contacts',
    label: 'Numa Contacts',
    section: 'features',
    group: 'modules',
    type: 'switch',
    default: false,
    help: 'People and companies, with cards, relationships and sharing.',
  },
  {
    key: 'contactsDefaultPhoneRegion',
    label: 'Phone region',
    section: 'features',
    childOf: 'contacts',
    type: 'text',
    default: '',
    placeholder: 'NZ',
    visibleWhen: contactsOn,
    // Mirrors the infra zod schema (strict) — a typo here would otherwise only
    // surface as a failed deploy at synth time.
    validate: (raw) =>
      raw === '' || /^[A-Z]{2}$/.test(raw) ? null : "Must be a 2-letter ISO country code (e.g. 'NZ')",
    help: 'The country a local phone number belongs to, as a 2-letter code. Empty: a local number without a country code is refused.',
  },
  {
    key: 'contactsTimezone',
    label: 'Timezone',
    section: 'features',
    childOf: 'contacts',
    type: 'text',
    default: '',
    placeholder: 'Pacific/Auckland',
    visibleWhen: contactsOn,
    validate: (raw) => {
      if (raw === '') return null;
      try {
        new Intl.DateTimeFormat('en', { timeZone: raw });
        return null;
      } catch {
        return "Must be a valid IANA timezone (e.g. 'Pacific/Auckland')";
      }
    },
    help: 'What "today" means for date reminders.',
  },
  {
    key: 'contactsPresence',
    label: 'Presence',
    section: 'features',
    childOf: 'contacts',
    type: 'switch',
    default: false,
    visibleWhen: contactsOn,
    help: 'Online, away or offline on each contact. Telephony needs it, and it works without telephony.',
  },
  // ── The Contacts telephony tree ────────────────────────────────────────────
  // Everything below telephony needs an Amazon Connect instance to mean
  // anything, so it hides until telephony is on.
  {
    key: 'contactsTelephony',
    label: 'Telephony',
    section: 'features',
    childOf: 'contacts',
    type: 'switch',
    default: false,
    visibleWhen: contactsOn,
    help: 'Phone calls from contact records: claimed numbers, pickup groups and the softphone. Creates billable AWS resources (an Amazon Connect instance, a key and any numbers claimed). Switching it off later does not release them.',
  },
  {
    key: 'connectAutoProvision',
    label: 'Create the Connect instance',
    section: 'features',
    childOf: 'contactsTelephony',
    type: 'switch',
    default: false,
    visibleWhen: contactsTelephonyOn,
    help: 'The next deploy creates the Amazon Connect instance. Off: the instance was made by hand.',
  },
  {
    key: 'connectClaimDid',
    label: 'Claim a phone number at deploy',
    section: 'features',
    childOf: 'contactsTelephony',
    type: 'switch',
    default: false,
    visibleWhen: contactsTelephonyOn,
    help: 'The deploy claims a billable phone number (only with the instance created at deploy). Off: claim numbers in the app.',
  },
  {
    key: 'connectInstanceUrl',
    label: 'Connect instance URL',
    section: 'features',
    childOf: 'contactsTelephony',
    type: 'text',
    default: '',
    visibleWhen: contactsTelephonyOn,
    placeholder: 'https://numa-<client>.my.connect.aws',
    help: 'Leave it empty when the deploy creates the instance. Never paste the /ccp-v2 address.',
  },
  {
    key: 'voiceLiveAssist',
    label: 'Live assist',
    section: 'features',
    childOf: 'contactsTelephony',
    type: 'switch',
    default: false,
    visibleWhen: contactsTelephonyOn,
    help: 'Suggestions during a call, from Contact Lens. Billed for each minute analysed. Off: a summary after the call only.',
  },
  {
    key: 'contactsCallLists',
    label: 'Call lists',
    section: 'features',
    childOf: 'contactsTelephony',
    type: 'switch',
    default: false,
    visibleWhen: contactsTelephonyOn,
    help: 'Ordered lists of people to call, with an outcome for each person.',
  },
  {
    key: 'contactsOpsLink',
    label: 'Call lists in Numa Ops',
    section: 'features',
    childOf: 'contactsTelephony',
    type: 'switch',
    default: false,
    visibleWhen: (cfg) => contactsTelephonyOn(cfg) && cfg.numaOps === true,
    help: 'A call list becomes an Ops zone with one ticket for each person, and outcomes sync both ways. Needs Numa Ops.',
  },
  {
    key: 'contactsCallIntelligence',
    label: 'Call intelligence',
    section: 'features',
    childOf: 'contactsTelephony',
    type: 'switch',
    default: false,
    visibleWhen: contactsTelephonyOn,
    help: 'Recording, transcription and analysis after the call. Billed in credits for each transcribed minute. Off: calls are placed and logged, and nothing is recorded.',
  },
  {
    key: 'contactsCallAnalytics',
    label: 'Call analytics',
    section: 'features',
    childOf: 'contactsCallIntelligence',
    type: 'switch',
    default: false,
    visibleWhen: (cfg) => contactsTelephonyOn(cfg) && cfg.contactsCallIntelligence === true,
    help: 'The Call Analytics page: outcomes, connect rates, effort for each person and the call log.',
  },
  {
    key: 'contactsAiCalls',
    label: 'AI calling (Arcanum only)',
    section: 'features',
    childOf: 'contactsTelephony',
    type: 'switch',
    default: false,
    visibleWhen: contactsTelephonyOn,
    help: 'A Numa agent places and answers calls, with a recording notice it always gives. Not for a customer until the security review is done.',
  },

  // ─── Features › Files ──────────────────────────────────────────────────────
  {
    key: 'numaDropZones',
    label: 'Drop Zones',
    section: 'features',
    group: 'files',
    type: 'switch',
    default: true,
    help: 'Shared upload folders that people outside the company can drop files into.',
  },
  {
    key: 'numaSharing',
    label: 'Sharing',
    section: 'features',
    group: 'files',
    type: 'switch',
    default: true,
    help: 'Share a document with someone outside the company, who can then ask questions about it.',
  },

  // ─── Features › Public pages ───────────────────────────────────────────────
  // Arcanum's limits for this client (Nathan, 2 Nov 2026). The deploy passes
  // them to the Lambdas that hold them (infra public-ai-step-construct.ts).
  {
    key: 'publicAiMaxDailyCalls',
    label: 'AI uses a day, all public pages',
    section: 'features',
    group: 'publicPages',
    type: 'number',
    default: '',
    placeholder: 'Empty: 3,000',
    validate: validateIntRange('AI uses a day', 1, 100_000),
    help: 'The most a company admin may set in Settings > Public AI.',
  },
  {
    key: 'publicAiMaxHourlyCredits',
    label: 'AI credits an hour, all public pages',
    section: 'features',
    group: 'publicPages',
    type: 'number',
    default: '',
    placeholder: 'Empty: 20',
    validate: validateIntRange('AI credits an hour', 1, 1_000),
    help: 'Billed credits. The alarm that turns public AI off for the company goes off at 1.5 times this figure.',
  },
  {
    key: 'publicAiMaxDailyCredits',
    label: 'AI credits a day, all public pages',
    section: 'features',
    group: 'publicPages',
    type: 'number',
    default: '',
    placeholder: 'Empty: 100',
    validate: validateIntRange('AI credits a day', 1, 10_000),
    help: 'Billed credits. The most a company admin may set in Settings > Public AI.',
  },
  {
    key: 'publicAiMaxOutputTokens',
    label: 'Longest AI answer (tokens)',
    section: 'features',
    group: 'publicPages',
    type: 'number',
    default: '',
    placeholder: 'Empty: 4,000',
    validate: validateIntRange('Longest AI answer', 100, 8_000),
    help: 'A token is about three quarters of a word. The most is 8,000.',
  },
  {
    key: 'publicFormVisitorHourlyCap',
    label: 'Form sends an hour, one visitor',
    section: 'features',
    group: 'publicPages',
    type: 'number',
    default: '',
    placeholder: 'Empty: 20',
    validate: validateIntRange('Form sends an hour, one visitor', 1, 10_000),
    help: 'One internet address is one visitor. An office or an event on one connection shares it.',
  },
  {
    key: 'publicFormVisitorDailyCap',
    label: 'Form sends a day, one visitor',
    section: 'features',
    group: 'publicPages',
    type: 'number',
    default: '',
    placeholder: 'Empty: 50',
    validate: validateIntRange('Form sends a day, one visitor', 1, 10_000),
  },
  {
    key: 'publicFormLinkHourlyCap',
    label: 'Form sends an hour, one link',
    section: 'features',
    group: 'publicPages',
    type: 'number',
    default: '',
    placeholder: 'Empty: 60',
    validate: validateIntRange('Form sends an hour, one link', 1, 10_000),
    help: "Each limit stays inside the link's daily limit, which its owner sets in Share (at most 500).",
  },

  // ─── Features › Security and network ───────────────────────────────────────
  {
    key: 'ssoEnabled',
    label: 'SSO self-service',
    section: 'features',
    group: 'security',
    type: 'switch',
    default: true,
    help: 'Admins set up SAML sign-in with their identity provider.',
  },
  {
    key: 'ssoEnterprise',
    label: 'SSO enterprise',
    section: 'features',
    childOf: 'ssoEnabled',
    type: 'switch',
    default: false,
    visibleWhen: (v) => v.ssoEnabled === true,
    help: 'Group mapping, OIDC, SSO-only sign-in and SCIM.',
  },
  {
    key: 'mfa',
    label: 'Multi-factor authentication',
    section: 'features',
    group: 'security',
    type: 'switch',
    default: false,
    help: 'Everyone signs in with a code from an authenticator app.',
  },
  {
    key: 'fixedEgressIp',
    label: 'Fixed outgoing IP',
    section: 'features',
    group: 'security',
    type: 'switch',
    default: false,
    help: "Numa's calls to the customer's own servers leave from one IP. Creates billable AWS resources (a small proxy, an Elastic IP and a VPC), billed in credits each month at cost plus 5%. No failover region. Switching it off releases the IP for good, and the customer's firewall rule then points at nothing.",
  },
  {
    key: 'fixedEgressRegion',
    label: 'Proxy region',
    section: 'features',
    childOf: 'fixedEgressIp',
    type: 'text',
    default: '',
    visibleWhen: (v) => v.fixedEgressIp === true,
    placeholder: 'The deploy region',
    help: "Run the proxy in another AWS region, nearer the customer's servers. Changing it replaces the proxy and its IP.",
  },
  {
    key: 'connectorClientCertificates',
    label: 'Connector certificates',
    section: 'features',
    group: 'security',
    type: 'switch',
    default: false,
    help: "Admins upload the client certificate the customer's own server asks for, and the CA that signed that server, for each connector (Settings > Network).",
  },

  // ─── Chat ──────────────────────────────────────────────────────────────────
  {
    key: 'defaultChatModel',
    label: 'Default chat model',
    section: 'chat',
    type: 'select',
    default: '',
    defaultLabel: 'platform default',
    dynamicOptions: (values) => chatModelDropdownOptions(values),
    hydrate: (config) => savedDefaultChatModel(config),
    effectiveCurrent: (config) => savedDefaultChatModel(config) || undefined,
    wide: true,
    help: 'The model new chats start on. Platform default saves nothing: the client follows what each release decides. Today that is Standard, or Premium when AWS-only models is on or global inference profiles are off.',
  },
  {
    key: 'workspaceChatModelSelection',
    label: 'Model picker in chat',
    section: 'chat',
    type: 'switch',
    default: true,
    help: 'People switch between the available models in chat.',
  },
  {
    key: 'bedrockModelsOnly',
    label: 'AWS-only models',
    section: 'chat',
    type: 'switch',
    default: false,
    help: 'Every model call stays on AWS Bedrock, and Lite is removed while its route is the relay. The platform default model becomes Premium. For customers who need all inference on AWS.',
  },
  {
    key: 'voiceRecordingDefaultMinutes',
    label: 'Voice recording limit (minutes)',
    section: 'chat',
    type: 'number',
    default: '',
    placeholder: '120',
    validate: validateVoiceRecordingMinutes,
    help: 'The longest voice recording a person can make, unless an admin sets a limit for their role or for them. Empty: 120. The most is 240.',
  },

  // ─── Advanced › Infrastructure ─────────────────────────────────────────────
  {
    key: 'agentCoreRegion',
    label: 'AgentCore region',
    section: 'advanced',
    group: 'infrastructure',
    type: 'select',
    default: '',
    defaultLabel: 'the deploy region',
    options: [{ label: 'Same as the deploy region', value: '' }, ...AGENTCORE_REGION_OPTIONS],
    help: 'Only when the deploy region has no Bedrock AgentCore.',
    warnWhenSet: 'Chat in another region adds latency. Change this only with a developer.',
  },
  {
    key: 'preferredKnowledgeBase',
    label: 'Files search backend',
    section: 'advanced',
    group: 'infrastructure',
    type: 'select',
    default: 'bedrock',
    dynamicOptions: (values) => [
      { label: 'Bedrock', value: 'bedrock' },
      {
        label: values.provisionQResources === true ? 'Q Business' : 'Q Business (switch on Provision Q resources)',
        value: 'q',
        disabled: values.provisionQResources !== true,
      },
      { label: 'None (no Files search)', value: 'none' },
    ],
    hydrate: (config) => effectiveKnowledgeBase(config),
    effectiveCurrent: (config) => effectiveKnowledgeBase(config),
    alwaysWrite: true,
    help: 'What powers Numa Files search.',
  },
  {
    key: 'provisionQResources',
    label: 'Provision Q resources',
    section: 'advanced',
    group: 'infrastructure',
    type: 'switch',
    default: false,
    alwaysWrite: true,
    help: 'Builds Amazon Q Business in this account. The Files search backend can be Q only while this is on.',
  },
  {
    key: 'useGlobalInferenceProfile',
    label: 'Global inference profiles',
    section: 'advanced',
    group: 'infrastructure',
    type: 'switch',
    default: true,
    help: "Off only when the client's AWS organisation blocks global Bedrock profiles. Off: no Lite, Standard and Premium run on regional profiles, and Premium becomes Opus 5.5 at low effort.",
  },
  {
    key: 'cognitoEmailBrandingOwner',
    label: 'Sign-in email owner',
    section: 'advanced',
    group: 'infrastructure',
    type: 'select',
    default: 'numa',
    options: [
      { label: 'Numa', value: 'numa' },
      { label: "The client's own app", value: 'client-app' },
    ],
    help: "Who owns the invitation and password-reset emails. Numa: each deploy sets them. The client's own app: a deploy sets them only when it creates the user pool, and the app's own setup stands after that (the Nolia stacks). A workspace whose email domain is on getnolia.io behaves this way already.",
    warnWhenSet:
      "Numa stops setting this pool's email handler, sender and key, so another stack must set them, and nothing here repairs them. Mail then goes from no-reply@verificationemail.com, capped at 50 emails a day, without Numa's signed domain. On a production workspace, agree that trade first.",
  },

  // ─── Advanced › Bedrock quota sharing ──────────────────────────────────────
  {
    key: 'bedrockAccount',
    label: "Use another account's Bedrock quotas",
    section: 'advanced',
    group: 'quota',
    type: 'text',
    default: '',
    placeholder: '12-digit AWS account ID',
    validate: validateAccountId,
    help: "The account whose Bedrock quotas this client uses. Empty: the client uses its own account's quotas.",
  },
  {
    key: 'allowBedrockQuotaSharing',
    label: "Allow other accounts to use this account's Bedrock quotas",
    section: 'advanced',
    group: 'quota',
    type: 'switch',
    default: false,
    help: 'Other Numa accounts may name this one above. Only one stack in each AWS account may have this on.',
  },

  // ─── Advanced › Emergency rollback ─────────────────────────────────────────
  {
    key: 'lightModelTransport',
    label: 'Lite route',
    section: 'advanced',
    group: 'rollback',
    type: 'select',
    default: 'bedrock',
    options: [
      { label: 'Bedrock, in this account', value: 'bedrock' },
      { label: 'Relay through OpenRouter (rollback)', value: 'relay' },
    ],
    help: 'How the Lite tier reaches GPT-6 Luna. The relay sends Lite traffic outside AWS.',
  },
  {
    key: 'standardModelTransport',
    label: 'Standard fallback route',
    section: 'advanced',
    group: 'rollback',
    type: 'select',
    default: 'bedrock',
    options: [
      { label: 'Bedrock, in this account', value: 'bedrock' },
      { label: 'Relay through OpenRouter (rollback)', value: 'relay' },
    ],
    help: 'Standard runs Claude Haiku 5.5, and this does not move it. It changes only how Standard reaches GPT-6 Luna, its fallback when Haiku fails. Ignored with AWS-only models on.',
  },

  // ─── Arcanum internal ──────────────────────────────────────────────────────
  {
    key: 'devInstance',
    label: 'Development instance',
    section: 'internal',
    type: 'switch',
    default: false,
    help: 'An Arcanum dev or demo stack: dev apps, the Expert model and the power-user screens.',
  },
  {
    key: 'internalOnly',
    label: 'Internal features',
    section: 'internal',
    type: 'switch',
    default: false,
    help: 'Arcanum-only features, today the Codebase Expert agent (chat over the Numa code, with GitLab merge requests).',
  },
  {
    key: 'noliaSmokeTest',
    label: 'Nolia smoke mode',
    section: 'internal',
    type: 'switch',
    default: false,
    help: 'Lets a Nolia activity run in smoke mode: every step runs one small, fast model (Claude Haiku 5.5) with no crew and no review, and every file keeps its shape, so a whole activity can be walked through the UI for a few dollars. Every document is stamped SMOKE TEST. The activity asks for it with its own setting; without this flag the agent refuses and runs the real pipeline. Dev stacks only.',
    warnWhenSet:
      'Smoke runs are not evaluations. Turn this on only for a development or test workspace, never one a client uses.',
  },
  {
    key: 'claudeCodeSetup',
    label: 'Connect Claude',
    section: 'internal',
    type: 'switch',
    default: false,
    help: 'The Connect Claude page: the guided install of the numa CLI and the Numa plugin for Claude Code. Internal testing only.',
  },
  {
    key: 'numaDesktop',
    label: 'Numa Desktop',
    section: 'internal',
    type: 'switch',
    default: false,
    help: "The desktop companion app that acts on the person's own machine (screenshots, keyboard, mouse, opening apps), with a prompt before each action. macOS only, internal testing.",
  },
];

/**
 * The fields of the Arcanum internal section. A customer stack with one of
 * them on still shows the section, so the switch can be seen and turned off.
 */
export const INTERNAL_FIELD_KEYS = FIELDS.filter((f) => f.section === 'internal').map((f) => f.key);

export const findField = (key: string): FieldDef | undefined => FIELDS.find((f) => f.key === key);

export const fieldsForSection = (section: SectionId): FieldDef[] => FIELDS.filter((f) => f.section === section);

// ---------------------------------------------------------------------------
// Hydration / serialization / diff helpers
// ---------------------------------------------------------------------------

/** Form value for a field given a config record (or undefined → defaults). */
export function hydrateFieldValue(def: FieldDef, config: Partial<ClientConfig> | undefined): FormFieldValue {
  if (def.hydrate) return def.hydrate(config);
  const raw = (config as Record<string, unknown> | undefined)?.[def.key];
  switch (def.type) {
    case 'switch':
      return typeof raw === 'boolean' ? raw : (def.default as boolean);
    case 'tri-state':
      return typeof raw === 'boolean' ? String(raw) : '';
    case 'number':
      return raw != null ? String(raw) : '';
    case 'text':
      return typeof raw === 'string' ? raw : (def.default as string);
    case 'select':
      return typeof raw === 'string' ? raw : (def.default as string);
  }
}

/** Initial form values for a whole record (Create mode passes undefined). */
export function hydrateFormValues(config: Partial<ClientConfig> | undefined): FormValues {
  const values: FormValues = {};
  for (const def of FIELDS) values[def.key] = hydrateFieldValue(def, config);
  return values;
}

/**
 * The default the portal PRESENTS for a field: `createDefault` when set, else
 * the blank-record schema default. Drives Create-mode initial values, the
 * per-field "Reset to default" link and the non-default (*) marker, so a flag
 * that is on for new clients also resets to ON rather than to the deploy-time
 * fallback. Deploy-time diffing (effectiveCurrentValue) is unaffected.
 */
export function presentedDefault(def: FieldDef): FormFieldValue {
  return def.createDefault ?? hydrateFieldValue(def, undefined);
}

/** Create-page initial values: schema defaults, overridden by createDefault. */
export function createModeInitialValues(): FormValues {
  const values: FormValues = {};
  for (const def of FIELDS) values[def.key] = presentedDefault(def);
  return values;
}

/** The labels of the required fields a new client's form has left empty. */
export function missingRequiredOnCreate(values: FormValues): string[] {
  return FIELDS.filter(
    (def) =>
      def.requiredOnCreate &&
      (!def.visibleWhen || def.visibleWhen(values)) &&
      serializeFieldValue(def, values[def.key]) === undefined
  ).map((def) => def.label);
}

/**
 * The config value a form value serializes to. `undefined` means "key absent"
 * (in Update mode that becomes a REMOVE expression via updateClientConfig).
 */
export function serializeFieldValue(def: FieldDef, value: FormFieldValue): unknown {
  switch (def.type) {
    case 'switch':
      return Boolean(value);
    case 'tri-state':
      return value === '' ? undefined : value === 'true';
    case 'number': {
      const s = String(value).trim();
      if (!s) return undefined;
      const n = parseInt(s, 10);
      return Number.isNaN(n) ? undefined : n;
    }
    case 'text': {
      const s = String(value).trim();
      return s === '' ? undefined : s;
    }
    case 'select': {
      const s = String(value);
      return s === '' ? undefined : s;
    }
  }
}

/**
 * The effective current value of a field for diffing — what the deployment
 * would use today. Switches/selects fall back to the display default when
 * the key is absent; optional text/number/tri-state fields stay undefined.
 */
export function effectiveCurrentValue(def: FieldDef, config: Partial<ClientConfig> | undefined): unknown {
  // Fields with a derived absent-value (e.g. defaultChatModel) resolve it here
  // so a pristine record that only inherits the derived value shows no diff.
  if (def.effectiveCurrent) return def.effectiveCurrent(config);
  const raw = (config as Record<string, unknown> | undefined)?.[def.key];
  switch (def.type) {
    case 'switch':
      return typeof raw === 'boolean' ? raw : (def.default as boolean);
    case 'select':
      return typeof raw === 'string' ? raw : serializeFieldValue(def, def.default);
    case 'tri-state':
    case 'number':
    case 'text':
      return raw ?? undefined;
  }
}

/** True when the field's form value differs from its presented default. */
export function isNonDefault(def: FieldDef, value: FormFieldValue): boolean {
  return value !== presentedDefault(def);
}

/**
 * An ON switch that Update/Create should force-write as an explicit `true`.
 *
 * On-by-default flags (switches with `default: true`) never appear in a stored
 * record when they simply inherit their default — the deployment reads `true`,
 * but config.json looks like the feature is unset. To keep config.json an honest
 * mirror of what's enabled, both payload builders materialize every switch that
 * is currently ON as `true`. OFF switches stay absent (writing every `false`
 * flag would bloat the record). Callers still guard on gate visibility and, in
 * Update mode, on the key being absent from the record.
 */
export function isOnSwitch(def: FieldDef, value: FormFieldValue): boolean {
  return def.type === 'switch' && value === true;
}

// ---------------------------------------------------------------------------
// Deprecated / dead keys — drives the Config Hygiene panel and the bulk
// tool's "Deprecated — cleanup" group. Removal uses the targeted REMOVE path
// (updateClientConfig with an undefined value), which does not require the
// remaining record to pass full-schema validation.
// ---------------------------------------------------------------------------

export interface DeprecatedKeyDef {
  key: string;
  reason: string;
  /** Extra caution when removal could change behavior for this record. */
  warnIf?: (config: Record<string, unknown>) => string | null;
}

const synergyFallbackWarning = (config: Record<string, unknown>): string | null => {
  // The infra fallback into `synergy` was removed with the KB-crawler cutover —
  // legacy keys are now IGNORED. Flag records that still look like they rely on
  // the old fallback so the operator sets `synergy` explicitly.
  if (config['synergy'] === undefined && (config['synergyKbCrawl'] === true || config['synergyFileParity'] === true)) {
    return 'Legacy Synergy keys are ignored now — set "synergy": true explicitly if this client should keep Synergy.';
  }
  return null;
};

export const DEPRECATED_KEYS: DeprecatedKeyDef[] = [
  {
    key: 'numaChatAgents',
    reason: 'Gated the deprecated Chat V1 agent; nothing reads it any more.',
  },
  {
    key: 'knowledgeBases',
    reason: 'Never read — the frontend KNOWLEDGE_BASES flag is hard-coded true at deploy time.',
  },
  {
    key: 'recordsKBEnabled',
    reason: 'Forward-compat stub from an abandoned branch; never implemented.',
  },
  {
    key: 'contentSearchEnabled',
    reason: 'Forward-compat stub from an abandoned branch; never implemented.',
  },
  {
    key: 'numaFiles',
    reason: 'Forward-compat stub from an abandoned branch; never implemented.',
  },
  {
    key: 'transcriptionService',
    reason: 'Forward-compat stub from an abandoned branch; never implemented.',
  },
  {
    key: 'jobHistory',
    reason: 'JOB_HISTORY is emitted to config.json but never read by the frontend.',
  },
  {
    key: 'synergyTermIndex',
    reason: 'Deprecated Synergy sub-flag; no longer read anywhere.',
  },
  {
    key: 'synergyFileParity',
    reason: 'Deprecated — replaced by the single "synergy" flag.',
    warnIf: synergyFallbackWarning,
  },
  {
    key: 'synergyKbCrawl',
    reason: 'Deprecated — replaced by the single "synergy" flag.',
    warnIf: synergyFallbackWarning,
  },
  {
    key: 'secretsVaultEnabled',
    reason: 'Deprecated (TASK-146) — the Secrets Vault follows Native Integrations; this value is ignored.',
  },
  {
    key: 'developerMode',
    reason: 'Merged into devInstance (FEAT-239) — Development Instance now also enables the power-user UI.',
    // Always warn (and stay out of "Remove all") until the merge is deployed
    // fleet-wide — an older image still reads this flag.
    warnIf: () =>
      'Remove only once this client runs an image with the FEAT-239 merge — older images still read this flag.',
  },
  {
    key: 'oauthIntegrationsEnabled',
    reason:
      'Merged into dataConnectorsEnabled (FEAT-239) — Native Integrations now gates the vault-backed OAuth connectors too.',
    warnIf: () =>
      'Remove only once this client runs an image with the FEAT-239 merge — older images still read this flag.',
  },
  {
    key: 'oauthProviders',
    reason: 'Legacy OAuth cloud-storage provider config, superseded by COMPANY vault secrets; nothing reads it.',
  },
  {
    key: 'enableOpenApiDocs',
    reason: 'Schema key exists but the deployment stack never wires it through; setting it has no effect.',
  },
  {
    key: 'logGroup',
    reason: 'Internal infra prop that leaked into the schema; must never be persisted in a client config.',
  },
  {
    key: 'workflowsAsApps',
    reason:
      'Deprecated (TKT-226) — the dashboards surface is always on and this value is ignored. App-shaped authoring is gated by appBuilder instead.',
  },
  {
    key: 'v2Apps',
    reason:
      'Deprecated (TASK-550) — V2 apps are no longer a category of their own and the value is ignored. Data Analysis, Nolia, Policy Designer and Quoting are app picks under Apps.',
  },
  {
    key: 'numaCommunity',
    reason:
      'Deprecated (FEAT-469) — the Numa Community has no per-client switch. Staff turn a workspace on by approving it in the Community console.',
    warnIf: () =>
      'Remove only once this client runs an image without the Community flag — older images still read it, and turn the Community off without it.',
  },
  {
    key: 'claudeImport',
    reason:
      'Deprecated (FEAT-575) — every client has Import from Claude and the value is ignored. An image from before Import from Claude refuses to deploy while the key is present.',
  },
  {
    key: 'claudeImportFullChats',
    reason:
      'Deprecated (FEAT-575) — full chats are always allowed and the value is ignored. An image from before Import from Claude refuses to deploy while the key is present.',
  },
  {
    key: 'numaWorkspaceChat',
    reason:
      'Every client has chat, and the portal no longer offers the switch. The deploy reads an absent key as on, so removing a stored "true" changes nothing.',
    warnIf: (config) =>
      config['numaWorkspaceChat'] === false
        ? 'This client has chat switched off. Removing the key turns chat on at its next deploy, which builds the chat runtime and starts its costs.'
        : null,
  },
  {
    key: 'numaVoice',
    reason:
      'Retired: telephony is part of Numa Contacts (Contacts and Telephony). The deploy still reads it as a fallback for the Amazon Connect instance.',
    warnIf: (config) =>
      config['numaVoice'] === true && !(config['contacts'] === true && config['contactsTelephony'] === true)
        ? 'This client has telephony only through this key. Removing it deletes its Amazon Connect instance, key and phone numbers at the next deploy. Switch on Contacts and Telephony first.'
        : null,
  },
  {
    key: 'backendServiceTokenMode',
    reason:
      'Ignored since 2026-10-05: the Nolia deploy sets backend trust through its own SSM parameter. Removing it changes nothing.',
  },
];

export const DEPRECATED_KEY_SET = new Set(DEPRECATED_KEYS.map((d) => d.key));

/** Every key the portal schema knows (deprecated keys included). */
export const KNOWN_CONFIG_KEYS = new Set(Object.keys(clientConfigSchema.shape));

export interface HygieneIssue {
  key: string;
  kind: 'deprecated' | 'unknown';
  reason: string;
  warning?: string;
  value: unknown;
}

/** Compute hygiene issues (deprecated + unknown keys) present in a record. */
export function computeHygieneIssues(config: Record<string, unknown>): HygieneIssue[] {
  const issues: HygieneIssue[] = [];
  for (const def of DEPRECATED_KEYS) {
    if (config[def.key] !== undefined) {
      issues.push({
        key: def.key,
        kind: 'deprecated',
        reason: def.reason,
        warning: def.warnIf?.(config) ?? undefined,
        value: config[def.key],
      });
    }
  }
  for (const key of Object.keys(config)) {
    // Deprecated keys already reported above — a key that has also been
    // deleted from the schema (e.g. developerMode) must not double-report.
    if (!KNOWN_CONFIG_KEYS.has(key) && !DEPRECATED_KEY_SET.has(key) && config[key] !== undefined) {
      issues.push({
        key,
        kind: 'unknown',
        reason: 'Not in the portal schema — possibly a typo, or a key from a newer schema version.',
        value: config[key],
      });
    }
  }
  return issues;
}

// ---------------------------------------------------------------------------
// Bulk Update tool derivation
// ---------------------------------------------------------------------------

export interface BulkFieldConfig {
  key: string;
  label: string;
  type: 'boolean' | 'enum' | 'string' | 'number' | 'json';
  options?: { label: string; value: string }[];
  description: string;
  allowUnset?: boolean;
  /** Deprecated-key cleanup entries: the ONLY valid operation is Unset. */
  removeOnly?: boolean;
  removeOnlyReason?: string;
}

/** Fields that exist only in the bulk tool (no single-client form control). */
const BULK_ONLY_FIELDS: BulkFieldConfig[] = [
  {
    key: 'customDomain',
    label: 'Custom Domain',
    type: 'string',
    allowUnset: true,
    description: 'Custom domain name — use Unset to fall back to the auto-generated domain',
  },
  {
    key: 'apps',
    label: 'Apps Configuration',
    type: 'json',
    description: 'JSON object mapping app IDs to config (e.g. {"meeting-analyser": {}})',
  },
  {
    key: 'allApps',
    label: 'Deploy All Apps',
    type: 'boolean',
    description: 'Deploy every app in the library (recommended for dev/demo stacks)',
  },
  {
    key: 'allProdApps',
    label: 'All Production Apps',
    type: 'boolean',
    description: 'Deploy all production applications',
  },
  {
    key: 'groups',
    label: 'Groups (Advanced)',
    type: 'json',
    allowUnset: true,
    description: 'JSON override for feature groups (e.g. {"standard":["chat"],"admin":["chat","manageUsers"]})',
  },
  {
    // An object, so the form has no control for it. This is the one editor
    // that sets it on a client without replacing the whole record.
    key: 'awsPartnerCentralConfig',
    label: 'AWS Partner Central connection (Numa Ops)',
    type: 'json',
    allowUnset: true,
    description:
      'The connection the AWS Partner Central switch uses. Alone it turns nothing on. e.g. {"linkedAccountId":"123456789012","roleArn":"arn:aws:iam::123456789012:role/NumaPartnerCentral-<Numa account id>","externalId":"<16 or more characters>","catalog":"Sandbox"}. Setup: tools/aws-partner-central/README.md',
  },
];

function bulkTypeFor(def: FieldDef): BulkFieldConfig['type'] {
  switch (def.type) {
    case 'switch':
      return 'boolean';
    case 'tri-state':
      return 'boolean';
    case 'number':
      return 'number';
    case 'select':
      return 'enum';
    case 'text':
      return 'string';
  }
}

function bulkAllowUnset(def: FieldDef): boolean {
  // region is schema-required; alwaysWrite keys are re-written by the Update
  // page on every save, so unsetting them in bulk would just churn.
  if (def.key === 'region' || def.alwaysWrite) return false;
  // Anything that serializes '' to "key absent" is unset-able, as are the
  // inherit-from-platform quota fields and tri-state (inherit) selects.
  return def.type !== 'switch';
}

/**
 * The bulk tool's field list, derived from the registry: every form field
 * (minus explicit bulk:false), plus the bulk-only JSON/string fields, plus
 * remove-only cleanup entries for the deprecated keys.
 */
export function getBulkUpdatableFields(): BulkFieldConfig[] {
  const fromRegistry: BulkFieldConfig[] = FIELDS.filter((f) => f.bulk !== false).map((def) => ({
    key: def.key,
    label: def.label,
    type: bulkTypeFor(def),
    // Selects with an '' (unset) option rely on the Unset checkbox in bulk —
    // never write '' as a real value.
    options: def.options?.filter((o) => o.value !== ''),
    description: def.help ?? def.label,
    allowUnset: bulkAllowUnset(def),
  }));
  const cleanup: BulkFieldConfig[] = DEPRECATED_KEYS.map((d) => ({
    key: d.key,
    label: `${d.key} (deprecated)`,
    type: 'json',
    description: d.reason,
    allowUnset: true,
    removeOnly: true,
    removeOnlyReason: d.reason,
  }));
  return [...fromRegistry, ...BULK_ONLY_FIELDS, ...cleanup];
}
